import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { EmbeddedPodlet, type EmbeddedPodlets, RenderCount } from "@mobility-item-page/client";
import { type DealerItemResult, resolveContactFormVariant } from "@mobility-item-page/dealer";
import { DealerItemDataProvider, DealerUIStateProvider } from "@mobility-item-page/dealer/client";
import type { AdData } from "@mobility-item-page/types";
import { AdPlacement, AdvertisingClientWebComponent } from "@schibsted-nmp/advertising-companion";
import { type ReactNode, useEffect } from "react";
import { PreviewBackgroundSVG } from "./assets/PreviewBackground.tsx";
import Ad from "./client/components/Ad.tsx";
import AdInfo from "./client/components/AdInfo.tsx";
import ErrorBoundary from "./client/components/ErrorBoundary.tsx";
import Navigation from "./client/components/Navigation.tsx";
import { Recommendations } from "./client/components/Recommendations.tsx";
import { PodletsProvider } from "./client/podlets.ts";
import { AdViewProvider } from "./context/AdViewProviderContext.tsx";
import { AppConfigProvider, type ClientAppConfig } from "./context/AppConfigProviderContext.tsx";
import type { SafetyTabs } from "./types/safetyElements.ts";
import { FeatureIds } from "./types/unleash.ts";
import type { PodletsNames } from "./utils/podlets.ts";

export type AppProps = {
  safetyElements: SafetyTabs[];
} & ProviderProps;

export const App = ({ safetyElements, ...providerProps }: AppProps) => {
  const { broadcast } = providerProps.podlets;
  const { isPreview } = providerProps.appConfig;

  useEffect(() => {
    if (isPreview) {
      const previewBackground = PreviewBackgroundSVG();
      document.body.style.backgroundImage = `url(${previewBackground})`;
      document.body.style.backgroundAttachment = "fixed";
      document.body.style.backgroundRepeat = "repeat";
    }
  }, [isPreview]);

  return (
    <RenderCount>
      <Providers {...providerProps}>
        <ErrorBoundary helpCenter={providerProps.appConfig.helpCenter}>
          <main
            // prevent FOUCE on w-icon: https://pages.github.schibsted.io/nmp-web/borealis-docs/news/fixing-cls-and-fouce/
            data-await-elements="w-icon"
            className={`page-container ${isPreview ? "!bg-transparent preview" : ""}`}
          >
            <EmbeddedPodlet response={broadcast} cautiouslyReloadScriptOnHydrationError />
            {!isPreview && <Navigation />}
            <AdInfo />
            <Ad safetyElements={safetyElements} />
            <Recommendations />
            <AdvertisingClientWebComponent placementId={AdPlacement.AdvtBottom1.id} />
          </main>
        </ErrorBoundary>
      </Providers>
    </RenderCount>
  );
};

type ProviderProps = {
  podlets: EmbeddedPodlets<PodletsNames>;
  adData: AdData;
  dealerItemResult: DealerItemResult | null;
  appConfig: ClientAppConfig;
  children?: ReactNode;
};

const Providers = ({ adData, appConfig, podlets, dealerItemResult, children }: ProviderProps) => {
  return (
    <I18nProvider i18n={i18n}>
      <PodletsProvider podlets={podlets}>
        <AdViewProvider adData={adData}>
          <DealerItemDataProvider
            dealerItemResult={dealerItemResult}
            external={{
              isMobile: appConfig.isMobile,
              brand: appConfig.brand,
              locale: appConfig.locale,
              isLoggedIn: appConfig.isLoggedIn,
              isPreview: appConfig.isPreview,
              adViewType: adData.ad.adViewType,
              homepageSurvey: appConfig.featureToggles[FeatureIds.DEALER_INFO_CARD_HOMEPAGE_SURVEY],
              reservationEnabled: appConfig.featureToggles[FeatureIds.ENABLE_CAR_RESERVATION_CTA],
              isItemReserved: false, // TODO: Replace this with actual reservation status from adData.ad,
              eidAuth: appConfig.eidAuth,
              newContactForm: appConfig.amplitudeExperiments?.["mobility-item-page-contact-form"] === "treatment",
              newContactFormV2: appConfig.amplitudeExperiments?.["mobility-item-page-contact-form-v2"] === "treatment",
              newContactFormV3: resolveContactFormVariant(
                appConfig.amplitudeExperiments?.["mobility-item-page-contact-form-v3"],
              ),
              forcePhoneExperiment:
                appConfig.amplitudeExperiments?.["mobility-item-page-force-phone-on-contact"] === "treatment",
              openContactForm: appConfig.openContactForm,
            }}
          >
            <DealerUIStateProvider>
              <AppConfigProvider appConfig={appConfig}>{children}</AppConfigProvider>
            </DealerUIStateProvider>
          </DealerItemDataProvider>
        </AdViewProvider>
      </PodletsProvider>
    </I18nProvider>
  );
};
